import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators'

import {
  selectActualLicenses,
  selectActualPermissions,
  selectIsActualCompanyDataReady,
} from 'containers/App/selectors'

import { fetchDataSuccess } from '../Integration/actions'

import {
  fetchPoliciesConfig,
  fetchPoliciesConfigError,
  fetchPoliciesConfigSuccess,
  fetchPoliciesSchemaError,
  fetchPoliciesSchemaSuccess,
  savePoliciesConfigError,
  savePoliciesConfigSuccess,
} from './actions'
import {
  FETCH_POLICIES_CONFIG_ASYNC,
  FETCH_POLICIES_SCHEMA_ASYNC,
  SAVE_POLICIES_CONFIG_ASYNC,
} from './constants'

const getCompanyId = (state$) => {
  const state = state$.value
  return (
    state.partnerOptions?.selectedCompany?.value ||
    state.app?.data?.id ||
    state.settings?.emailEncryption?.integration?.data?.response?.tenantId ||
    state.settings?.emailEncryption?.integration?.data?.tenantId ||
    ''
  )
}

const getUserEmail = (state$) => {
  const state = state$.value
  return state.app?.data?.user?.email
}

// Mirrors oes_management_proxy/views.py's `_OesManagementProxyBaseView`
// permission_classes: AdminPermission + IsEmailEncryptionEnabledPermission.
// `licenses.email_encryption_settings_visible` is the only FE-exposed signal for
// email-encryption entitlement (same field the nav uses, see
// ui/src/config/navigation/navPredicates.js emailEncryptionVisible). Uses the
// ACTUAL (MSP-selected) company's admin permission, not the self permission —
// the backend's PartnerSwapPermission swaps to the child company before
// AdminPermission runs, so an MSP admin viewing a non-admin child must be denied.
const canUseEmailEncryptionCompanyPolicies = (state) =>
  Boolean(selectActualPermissions(state)?.admin) &&
  Boolean(selectActualLicenses(state)?.email_encryption_settings_visible)

// A synthetic, client-side 403 — no request was made, so nothing to report to
// Sentry. `[403]` marks it expected so epicsErrorHandler/store.js treats it like
// any other expected API error instead of an unhandled one.
const accessDeniedError = () => ({ status: 403, response: {} })

// Returns null when it's safe to proceed, or an error action to emit instead of
// calling the API: a synthetic 403 once actual-company data is ready and the
// gate fails, or nothing at all while actual-company data isn't ready yet (the
// gate can't be evaluated reliably against a still-loading MSP child, so it must
// not deny — see selectIsActualCompanyDataReady).
const denyIfGated = (state, errorAction) => {
  if (!selectIsActualCompanyDataReady(state)) return null
  if (canUseEmailEncryptionCompanyPolicies(state)) return null
  return errorAction(accessDeniedError())
}

const fetchSchemaEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_POLICIES_SCHEMA_ASYNC.INITIATE),
    switchMap(() => {
      const state = state$.value
      const denied = denyIfGated(state, (error) =>
        epicsErrorHandler(fetchPoliciesSchemaError, error, [403])
      )
      if (denied) return of(denied)

      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionPolicyCategories(companyId).pipe(
        map((response) => fetchPoliciesSchemaSuccess(response?.data ?? response)),
        catchError((error) => of(epicsErrorHandler(fetchPoliciesSchemaError, error)))
      )
    })
  )

const savePoliciesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_POLICIES_CONFIG_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const denied = denyIfGated(state, (error) =>
        epicsErrorHandler(savePoliciesConfigError, error, [403])
      )
      if (denied) return of(denied)

      const companyId = getCompanyId(state$)
      const userEmail = getUserEmail(state$)

      const currentConfig = state.settings?.emailEncryption?.integration?.data
      const isNewConfig = !currentConfig || Object.keys(currentConfig).length === 0

      const finalPayload = {
        ...action.payload,
        user: userEmail,
      }

      const apiCall = isNewConfig
        ? api.settings.createEmailEncryptionConfig(companyId, finalPayload)
        : api.settings.updateEmailEncryptionConfig(companyId, finalPayload)

      return apiCall.pipe(
        mergeMap((response) => [savePoliciesConfigSuccess(response), fetchPoliciesConfig()]),
        catchError((error) => of(epicsErrorHandler(savePoliciesConfigError, error)))
      )
    })
  )

const fetchConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_POLICIES_CONFIG_ASYNC.INITIATE),
    switchMap(() => {
      const state = state$.value
      const denied = denyIfGated(state, (error) =>
        epicsErrorHandler(fetchPoliciesConfigError, error, [403])
      )
      if (denied) return of(denied)

      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionConfig(companyId).pipe(
        mergeMap((response) => {
          const config = response?.data ?? response
          return [fetchDataSuccess(config), fetchPoliciesConfigSuccess()]
        }),
        catchError((error) => of(epicsErrorHandler(fetchPoliciesConfigError, error)))
      )
    })
  )

export default combineEpics(fetchSchemaEpic, fetchConfigEpic, savePoliciesEpic)
